import { AdminEmailPreview } from '../../../components/admin/AdminEmailEditor';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  DialogTitle,
  FormControl,
  FormLabel,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Typography,
} from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import { AdminCXAPI, cxError } from '../../../lib/apiCX';
import { CrmTemplatesAPI } from '../../../lib/apiCrmTemplates';
import { AdminOnboardingAPI } from '../../../lib/apiAdmin';
import type { CustomerRecord } from '../../../types/admin';
import type { CXMessagePreviewInput, CXTemplateField } from '../../../types/cx';
import { AIDraft } from './AIDraft';

export function TemplateFieldGuide({ scope = 'onboarding' }: { scope?: 'onboarding' | 'crm' }) {
  const fields = useQuery({
    queryKey: ['admin', scope === 'crm' ? 'crm' : 'cx', 'template-fields'],
    queryFn: scope === 'crm' ? CrmTemplatesAPI.fields : AdminCXAPI.templateFields,
    staleTime: Infinity,
  });
  const [notice, setNotice] = useState('');
  const copy = async (field: CXTemplateField) => {
    try {
      await navigator.clipboard.writeText(field.code);
      setNotice(`Copied ${field.code}. Paste it into your subject or message.`);
    } catch {
      setNotice(`Copy this code manually: ${field.code}`);
    }
  };
  const codeButton = (field: CXTemplateField) => (
    <Button
      key={field.key}
      size="sm"
      variant="outlined"
      onClick={() => copy(field)}
      aria-label={`Copy ${field.code}`}
      sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}
    >
      {field.code}
    </Button>
  );
  return (
    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'md', display: 'grid', gap: 1 }}>
      <Typography level="title-sm">Personalization fields</Typography>
      <Typography level="body-sm">
        {scope === 'crm'
          ? 'Use the same codes as onboarding in your subject and message. Click a code to copy it. Selecting a template fills in the selected recipient’s details before you send.'
          : 'Use these codes in email subjects, email content, and SMS drafts. Click a code to copy it. Names and links are filled in for each customer.'}
      </Typography>
      {fields.isPending && <LinearProgress />}
      {fields.error && (
        <Alert color="danger">
          Could not load the field guide.
          <Button size="sm" onClick={() => fields.refetch()}>
            Retry
          </Button>
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {fields.data?.fields
          .filter((field) => ['first_name', 'company_name', 'company'].includes(field.key))
          .map(codeButton)}
      </Box>
      <Typography level="body-sm">
        {
          'Example: Hi {{first_name}}, welcome to {{company_name}}! Spaces inside the braces are optional. {{company}} also means the company name.'
        }
      </Typography>
      {fields.data && (
        <Box component="details">
          <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 'lg' }}>
            All {fields.data.fields.length} fields, examples, and missing-value rules
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 1.5,
              mt: 1.5,
            }}
          >
            {fields.data.fields.map((field) => (
              <Box
                key={field.key}
                sx={{
                  minWidth: 0,
                  display: 'grid',
                  gap: 0.5,
                  justifyItems: 'start',
                }}
              >
                <Typography level="title-sm">{field.label}</Typography>
                {codeButton(field)}
                <Typography level="body-xs" sx={{ overflowWrap: 'anywhere' }}>
                  {field.description} Example: {field.example}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  {field.fallback}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {notice && (
        <Typography role="status" level="body-sm">
          {notice}
        </Typography>
      )}
    </Sheet>
  );
}

export function MessagePreview({
  title,
  draftMode = 'custom',
  ...input
}: CXMessagePreviewInput & { title: string; draftMode?: 'custom' | 'onboarding' }) {
  const [open, setOpen] = useState(false);
  const [customer, setCustomer] = useState<CustomerRecord | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const customers = useQuery({
    queryKey: ['admin', 'cx', 'preview-customers', debouncedSearch],
    queryFn: () =>
      AdminOnboardingAPI.listCustomers({
        search: debouncedSearch,
        page_size: 25,
      }),
    enabled: open,
  });
  const preview = useQuery({
    queryKey: ['admin', 'cx', 'message-preview', input, customer?.id],
    queryFn: () =>
      AdminCXAPI.previewMessage({
        ...input,
        customer_id: customer?.id ?? null,
      }),
    enabled: open,
    retry: false,
    staleTime: 0,
  });
  return (
    <>
      <Button variant="outlined" size="sm" onClick={() => setOpen(true)} aria-label={`Preview ${title}`}>
        {draftMode === 'onboarding' ? 'Choose customer & draft follow-up' : 'Preview message'}
      </Button>
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog
          sx={{
            width: 640,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: '85dvh',
            overflow: 'auto',
          }}
        >
          <ModalClose />
          <DialogTitle sx={{ pr: 3 }}>{title} — preview</DialogTitle>
          <Typography level="body-sm">
            Preview only. Nothing is saved or sent. Clear the customer to use Dana Reed at Summit Roofing as
            sample data.
          </Typography>
          {draftMode === 'onboarding' && (
            <Alert color="primary">
              Choose an account, then select Create AI draft. ApexOS chooses a relevant follow-up from that
              account’s saved steps. You do not need to write a fixed message here.
            </Alert>
          )}
          <FormControl>
            <FormLabel>Preview customer</FormLabel>
            <Autocomplete
              placeholder="Sample: Dana Reed at Summit Roofing"
              value={customer}
              options={customers.data?.results ?? []}
              filterOptions={(options) => options}
              getOptionLabel={(option) => option.organization_name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => setCustomer(value)}
              onInputChange={(_, value) => setSearch(value)}
              loading={customers.isFetching}
            />
          </FormControl>
          {customers.error && (
            <Alert color="warning">
              Customer search could not load.
              <Button size="sm" onClick={() => customers.refetch()}>
                Retry
              </Button>
            </Alert>
          )}
          {preview.isFetching ? (
            <LinearProgress />
          ) : preview.error ? (
            <Alert color="danger">
              {cxError(preview.error)}
              <Button size="sm" onClick={() => preview.refetch()}>
                Retry preview
              </Button>
            </Alert>
          ) : (
            preview.data && (
              <Box sx={{ display: 'grid', gap: 2 }}>
                {preview.data.warnings.length > 0 && (
                  <Alert color="warning">
                    <Box>
                      <Typography level="title-sm">Some details are missing</Typography>
                      {preview.data.warnings.map((warning) => (
                        <Typography key={warning} level="body-sm">
                          {warning}
                        </Typography>
                      ))}
                    </Box>
                  </Alert>
                )}
                {input.subject !== undefined && (
                  <Box>
                    <Typography level="title-sm">Subject</Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {preview.data.subject || '(Empty subject)'}
                    </Typography>
                  </Box>
                )}
                {(draftMode !== 'onboarding' || preview.data.body) && (
                  <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                      {draftMode === 'onboarding' ? 'Optional wording reference' : 'Message'}
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {preview.data.body || '(Empty message)'}
                    </Typography>
                  </Sheet>
                )}
                {(preview.data.preview_html || preview.data.body_html) && <AdminEmailPreview html={preview.data.preview_html || preview.data.body_html || ''} />}
                {preview.data.body_html && (
                  <Box component="details">
                    <summary>Personalized HTML source</summary>
                    <Box
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        fontSize: 'sm',
                      }}
                    >
                      {preview.data.body_html}
                    </Box>
                  </Box>
                )}
                {customer && (
                  <AIDraft
                    key={customer.id}
                    input={{
                      customer_id: customer.id,
                      channel: input.subject !== undefined ? 'email' : 'sms',
                      purpose: title,
                      mode: draftMode,
                      subject: input.subject,
                      body: input.body,
                      config: input.config,
                    }}
                  />
                )}
                {!customer && (
                  <Typography level="body-xs">
                    Choose a customer above to try an AI-personalized draft with a relevant next step.
                  </Typography>
                )}
              </Box>
            )
          )}
        </ModalDialog>
      </Modal>
    </>
  );
}
